// ============================================================
// A FUNÇÃO E AS SKILLS DO PROFISSIONAL (Story 31.5, Epic 31)
//
// Duas dimensões, e não tags planas: tags não respondem a «desenvolvedores →
// backend → backend C#».
//   • FUNÇÃO — hierárquica, em dois níveis: a ÁREA (`work_areas`, «Desenvolvimento»)
//     e a ESPECIALIDADE (`work_roles`, «Backend»). O profissional tem uma principal
//     e outras secundárias, e é sempre uma especialidade.
//   • SKILLS — de um catálogo com SINÔNIMOS («C#», «csharp» e «c sharp» são a mesma),
//     cada uma com o nível de 1 a 4 e, se se souber, os anos.
//
// Este ficheiro é o contrato entre a tela e a API: a chave dos sinônimos
// (`normalizeSkillTerm`), e os corpos dos pedidos. O catálogo vive na base (semente
// na migration 0056); os rótulos de nível e de senioridade vivem na tela.
// ============================================================
import { z } from 'zod'

import { ProfessionalSenioritySchema, type ProfessionalSeniority } from './dtos.ts'

/** O nível de uma skill: 1 Básico, 2 Autônomo, 3 Avançado, 4 Referência (plano do #467). */
export const SKILL_LEVEL_MIN = 1
export const SKILL_LEVEL_MAX = 4

/**
 * A NOTA MÍNIMA (0–10) de um tópico de entrevista que SUSTENTA cada nível (Story
 * 31.7, a tabela registada no #474). Uma tabela só, para três leituras: o nível que
 * a sugestão propõe, o ✓ da lista («provada no nível mostrado») e o filtro «só
 * evidenciadas» («C# ≥ Avançado» exige uma prova de 6.5 ou mais). A pessoa escolhe o
 * nível que quiser; a prova é que só conta até onde a nota chega.
 */
export const SKILL_LEVEL_SCORE_FLOOR: Readonly<Record<number, number>> = {
  1: 0,
  2: 4,
  3: 6.5,
  4: 8.5,
}

/**
 * A nota de uma prova, com UMA casa — arredondada na origem, antes de se guardar, e
 * por isso a mesma no selo («8.5»), no nível sugerido e na comparação do SQL. Com a
 * nota crua, um 8.46 aparecia como «8.5» e sugeria Avançado.
 */
export function roundEvidenceScore(score: number): number {
  return Math.round(score * 10) / 10
}

/** O nível mais alto que uma nota sustenta. */
export function skillLevelFromScore(score: number): number {
  for (let nivel = SKILL_LEVEL_MAX; nivel > SKILL_LEVEL_MIN; nivel--) {
    if (score >= SKILL_LEVEL_SCORE_FLOOR[nivel]!) return nivel
  }
  return SKILL_LEVEL_MIN
}

/** Para agrupar o catálogo na tela. Uma skill criada sem categoria fica em `outra`. */
export const SKILL_CATEGORIES = [
  'linguagem',
  'framework',
  'dados',
  'cloud',
  'ferramenta',
  'pratica',
  'outra',
] as const
export type SkillCategory = (typeof SKILL_CATEGORIES)[number]

export const SKILL_NAME_MAX = 60

/**
 * Os tetos do `PUT /api/professionals/:id/expertise`. Não são um plano de carreira:
 * cada linha liga parâmetros, e o D1 corta aos 100 por instrução — a rota parte as
 * inserções, e o teto mantém o pedido num tamanho que uma pessoa preenche.
 */
export const EXPERTISE_ROLES_MAX = 10
export const EXPERTISE_SKILLS_MAX = 40

/**
 * A CHAVE de um termo do catálogo: minúsculas, sem acentos, e sem espaços, pontos,
 * hífens nem sublinhados. É ela que faz «C Sharp», «c-sharp» e «CSharp» serem a
 * mesma coisa, e «Node.js» e «node js» também.
 *
 * ⚠️ `#` E `+` FICAM: são o que distingue «C#» e «C++» de «C». Por isso «C#» não
 * colapsa sozinho em «csharp» — os dois são sinônimos da mesma skill na semente.
 *
 * NFKD, e não NFD (revisão de segurança da 31.5): a decomposição de COMPATIBILIDADE
 * dobra a largura total, e «Ｃ＃» cai no C# em vez de nascer como uma skill à parte
 * que o filtro da 31.6 nunca encontraria. Homóglifos de outro alfabeto (o «С»
 * cirílico) continuam distintos — o remédio para esses é a fusão.
 *
 * ⚠️ A SEMENTE DA 0056 GUARDA AS CHAVES JÁ NORMALIZADAS, em SQL escrito à mão. Um
 * teste aplica a cadeia de migrations e exige `normalizeSkillTerm(alias) === alias`
 * em cada uma: mudar esta função sem mudar a semente fica vermelho.
 */
export function normalizeSkillTerm(termo: string): string {
  return termo
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[\s._-]+/g, '')
}

/** O nome que uma pessoa escreve: aparado, curto, sem caracteres de controle, e com chave. */
const NomeDeSkill = z
  .string()
  .trim()
  .min(1)
  .max(SKILL_NAME_MAX)
  .refine((nome) => !/\p{C}/u.test(nome), { message: 'O nome tem caracteres inválidos.' })
  .refine((nome) => normalizeSkillTerm(nome) !== '', {
    message: 'O nome precisa ter letras ou números.',
  })

/**
 * Criar uma skill — qualquer usuário da Genesis (decisão do owner no #467). Um nome
 * cuja chave já existe no catálogo NÃO cria nada: a API devolve a skill existente.
 */
export const CreateSkillSchema = z
  .object({
    name: NomeDeSkill,
    category: z.enum(SKILL_CATEGORIES).default('outra'),
  })
  .strict()
export type CreateSkill = z.infer<typeof CreateSkillSchema>

/** Fundir uma skill noutra — só admin. A de origem deixa de existir. */
export const MergeSkillSchema = z.object({ intoSkillId: z.string().min(1).max(64) }).strict()

const Id = z.string().min(1).max(64)

/**
 * A função e as skills de um profissional, SUBSTITUÍDAS por inteiro (o formulário
 * envia o estado todo). Validado aqui, à entrada, para nunca chegar à base um pedido
 * que só a FK recusaria (um 500 em vez de um 422 com o motivo).
 */
export const PutExpertiseSchema = z
  .object({
    roles: z
      .array(z.object({ roleId: Id, primary: z.boolean() }).strict())
      .max(EXPERTISE_ROLES_MAX),
    skills: z
      .array(
        z
          .object({
            skillId: Id,
            level: z.number().int().min(SKILL_LEVEL_MIN).max(SKILL_LEVEL_MAX),
            years: z.number().int().min(0).max(60).nullable().optional(),
          })
          .strict(),
      )
      .max(EXPERTISE_SKILLS_MAX),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (new Set(v.roles.map((r) => r.roleId)).size !== v.roles.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['roles'],
        message: 'A mesma função aparece duas vezes.',
      })
    }
    if (v.roles.length > 0 && v.roles.filter((r) => r.primary).length !== 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['roles'],
        message: 'Uma das funções precisa ser a principal — e só uma.',
      })
    }
    if (new Set(v.skills.map((s) => s.skillId)).size !== v.skills.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['skills'],
        message: 'A mesma skill aparece duas vezes.',
      })
    }
  })
export type PutExpertise = z.infer<typeof PutExpertiseSchema>

/**
 * Uma skill do catálogo, como a API a devolve — com os SINÔNIMOS já normalizados: a
 * tela procura neles com `normalizeSkillTerm` («csharp» encontra «C#») sem uma ida
 * ao servidor por tecla. O catálogo é curado e pequeno, e vem inteiro.
 */
export interface SkillDto {
  id: string
  name: string
  category: SkillCategory
  aliases: string[]
}

/**
 * Uma linha da tela de administração das skills (#485): a skill, com os sinônimos e
 * QUANTOS profissionais a têm — o número que a fusão diz antes de reescrever as
 * skills dessas pessoas. Conta toda a gente, os arquivados incluídos: a fusão muda as
 * linhas deles também.
 */
export interface SkillCatalogEntry extends SkillDto {
  professionals: number
}

/** O catálogo de funções: as áreas, cada uma com as suas especialidades. */
export interface WorkRoleCatalog {
  areas: { id: string; name: string; roles: { id: string; name: string }[] }[]
}

/**
 * A PROVA de uma skill (Story 31.7, migration 0057): a entrevista de onde veio, a
 * nota do tópico (0–10, a escala da avaliação) e quando foi concluída (ISO-8601).
 * Uma skill com prova é «evidenciada»; sem prova, é DECLARADA.
 */
export interface SkillEvidence {
  evaluationId: string
  score: number
  at: string
}

/** A função e as skills de um profissional, como a API as devolve. */
export interface ProfessionalExpertise {
  roles: { roleId: string; roleName: string; areaId: string; areaName: string; primary: boolean }[]
  skills: {
    skillId: string
    name: string
    category: SkillCategory
    level: number
    years: number | null
    evidence: SkillEvidence | null
  }[]
}

/**
 * Uma SUGESTÃO para a função, as skills ou a senioridade (Story 31.7). Calcula-se na
 * leitura e nunca se grava sozinha: só o «aceitar» a torna dado.
 *
 *   • `skill` com `evidence` — veio de um tópico da entrevista VIGENTE de uma
 *     candidatura; `topic` diz qual. Com `current`, a pessoa já tem a skill: aceitar
 *     põe (ou ATUALIZA) a prova, e nunca cria uma segunda linha.
 *   • `skill` sem `evidence`, `role` e `seniority` — vieram do cargo atual.
 *
 * `level` é o nível sugerido; a pessoa ajusta-o antes de aceitar.
 */
export type ExpertiseSuggestion =
  | {
      kind: 'skill'
      skillId: string
      name: string
      level: number
      evidence: SkillEvidence | null
      topic: string | null
      current: { level: number; evidence: SkillEvidence | null } | null
    }
  | { kind: 'role'; roleId: string; roleName: string; areaName: string }
  | { kind: 'seniority'; seniority: ProfessionalSeniority }

/** Um tópico das entrevistas vigentes que não casou com nenhuma skill — só informação. */
export interface UnmatchedTopic {
  topic: string
  score: number
  at: string
}

export interface ProfessionalSuggestions {
  suggestions: ExpertiseSuggestion[]
  unmatchedTopics: UnmatchedTopic[]
}

/**
 * Aceitar uma sugestão. O pedido diz QUAL e, numa skill, o nível escolhido — e mais
 * nada: a prova (avaliação, nota, data) o servidor recalcula, e uma sugestão que já
 * não existe é recusada. O cliente não escreve evidência.
 *
 * `evidenceEvaluationId` é a prova que a pessoa VIU (`null` numa skill declarada), e
 * serve SÓ para comparar (revisão de código da 31.7): se a vigente mudou entre a
 * leitura e o clique — um «fixar», um reprocessamento —, a sugestão é outra, e o
 * nível foi escolhido a olhar para uma nota que já não vale. O servidor recusa.
 */
export const AcceptSuggestionSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('skill'),
      skillId: Id,
      level: z.number().int().min(SKILL_LEVEL_MIN).max(SKILL_LEVEL_MAX),
      evidenceEvaluationId: Id.nullable(),
    })
    .strict(),
  z.object({ kind: z.literal('role'), roleId: Id }).strict(),
  z.object({ kind: z.literal('seniority'), seniority: ProfessionalSenioritySchema }).strict(),
])
export type AcceptSuggestion = z.infer<typeof AcceptSuggestionSchema>
